(() => {
  if (!customElements.get('wh-faq-groups')) {
    customElements.define('wh-faq-groups', class extends HTMLElement {
      connectedCallback() {
        this.updateNavigation();
        this.observer = new MutationObserver(() => this.updateNavigation());
        this.observer.observe(this, { childList: true, subtree: true, characterData: true });
      }

      updateNavigation() {
        const nav = this.closest('.wh-faq')?.querySelector('[data-wh-faq-nav]');
        if (!nav) return;
        const links = [];
        this.querySelectorAll('.wh-faq__group').forEach((group) => {
          const heading = group.querySelector('[data-faq-heading]')?.textContent.trim();
          if (!heading || !group.querySelector('.wh-faq__item')) return;
          const link = document.createElement('a');
          link.href = `#${group.id}`;
          link.append(document.createTextNode(`${heading} `));
          const arrow = document.createElement('span');
          arrow.textContent = '↓';
          arrow.setAttribute('aria-hidden', 'true');
          link.append(arrow);
          links.push(link);
        });
        nav.replaceChildren(...links);
        nav.hidden = links.length === 0;
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    });
  }

  if (customElements.get('wh-faq-accordion')) return;

  class FAQItem {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector('summary');
      this.expanded = details.open;
      this.animation = null;
    }

    setExpanded(expanded) {
      const startHeight = this.details.getBoundingClientRect().height;
      this.expanded = expanded;
      this.cancelAnimation();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.finish();
        return;
      }

      // Keep the answer rendered until the closing animation has finished.
      this.details.open = true;
      const styles = getComputedStyle(this.details);
      const borderHeight = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      const endHeight = expanded
        ? this.details.getBoundingClientRect().height
        : this.summary.getBoundingClientRect().height + borderHeight;

      this.details.style.overflow = 'hidden';
      const animation = this.details.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 280, easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)', fill: 'both' }
      );
      this.animation = animation;
      animation.onfinish = () => {
        if (this.animation === animation) this.finish();
      };
    }

    cancelAnimation() {
      if (this.animation) {
        this.animation.onfinish = null;
        this.animation.cancel();
        this.animation = null;
      }
      this.details.style.removeProperty('overflow');
    }

    finish() {
      this.details.open = this.expanded;
      this.cancelAnimation();
    }
  }

  customElements.define('wh-faq-accordion', class extends HTMLElement {
    connectedCallback() {
      if (this.controller || !Element.prototype.animate) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.items = new Map();
      // Delegation also handles questions inserted or moved by the theme editor.
      this.addEventListener('click', (event) => {
        const summary = event.target.closest('summary');
        if (!summary || summary.closest('wh-faq-accordion') !== this) return;
        const details = summary.closest('.wh-faq__item');
        if (!details) return;
        event.preventDefault();
        const item = this.getItem(details);
        item.setExpanded(!item.expanded);
      }, { signal });

      // A changed viewport must not leave an answer with an outdated fixed height.
      window.addEventListener('resize', () => this.items.forEach((item) => item.finish()), { signal });
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
        this.items.forEach((item) => item.finish());
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const details = event.target.closest('.wh-faq__item');
        if (details && this.contains(details)) {
          const item = this.getItem(details);
          item.expanded = true;
          item.finish();
        }
      }, { signal });
    }

    getItem(details) {
      if (!this.items.has(details)) this.items.set(details, new FAQItem(details));
      return this.items.get(details);
    }

    disconnectedCallback() {
      this.items?.forEach((item) => item.finish());
      this.controller?.abort();
      this.controller = null;
    }
  });
})();
